/**
 * @fileoverview SessionViewerPool Component
 * 
 * Renders all SessionViewer instances into react-reverse-portal InPortals.
 * This component is separated to isolate re-renders from the parent provider tree.
 * 
 * **Performance Optimization:**
 * 
 * This component subscribes only to the session SID list (not the full sessions object).
 * It re-renders only when sessions are added/removed, focus changes, visibility changes,
 * or audio devices change. Per-session property updates (noise, flags, sync data) do NOT
 * trigger re-renders here — those are handled by individual subscriber components.
 * 
 * Session data is read via getState() at render time, which provides the latest values
 * without creating a subscription that would cause re-renders on every property change.
 * 
 * **How React-Reverse-Portal Works Here:**
 * 
 * Each SessionViewer is rendered into an InPortal with a stable portal node.
 * React's reconciliation with `key={sessionSid}` ensures:
 * - Existing SessionViewer components are REUSED (same key = same instance)
 * - New sessions trigger component CREATION
 * - Removed sessions trigger component UNMOUNTING
 * 
 * The InPortal renders components into detached DOM nodes that can be moved
 * anywhere using OutPortals without remounting or losing state.
 * 
 * @module vtc/contexts/SessionViewerContext/SessionViewerPool
 * @private
 */

import React, { useEffect, useMemo, useRef } from 'react';
import * as portals from 'react-reverse-portal';
import { SessionViewer } from '@shared/components/proctoring/SessionViewer';
import useSessionStore from '../../stores/session';
import useAppStore from '../../stores/app';
import { getEnabledComponentsForSession } from './utils';

/**
 * SessionViewerPool Component
 * 
 * Renders all SessionViewer instances into InPortals. This component
 * subscribes only to the session SID list and key triggers (focus, visibility,
 * audio devices) — NOT the full sessions object. This prevents re-renders when
 * per-session properties change (noise, flags, sync data, etc.).
 * 
 * @component
 * @param {Object} props
 * @param {React.MutableRefObject} props.portalNodesRef - Ref object storing portal nodes
 * @param {Function} props.getOrCreatePortalNode - Function to get/create portal node for a session
 * @returns {JSX.Element}
 * 
 * @example
 * <SessionViewerPool
 *   portalNodesRef={portalNodesRef}
 *   getOrCreatePortalNode={getOrCreatePortalNode}
 * />
 */
export function SessionViewerPool({ portalNodesRef, getOrCreatePortalNode }) {
    // Subscribe only to the session SID list — NOT the full sessions object.
    // This produces a new string value only when sessions are added or removed,
    // avoiding re-renders when per-session properties (noise, flags, sync data) change.
    const sessionSids = useSessionStore(
        (state) => Object.keys(state.sessions).join(',')
    );

    // Subscribe to a lightweight location signature that only changes when session
    // locations change (e.g., waiting → testing). This is a safety net to ensure
    // the pool re-renders on location transitions, alongside the visibleSessions
    // subscription which already covers this in practice.
    // eslint-disable-next-line no-unused-vars
    const locationSignature = useSessionStore(
        (state) => Object.entries(state.sessions)
            .map(([sid, s]) => `${sid}:${s.location}`)
            .join(',')
    );

    // ALSO subscribe to focusedSessionSid so we re-render when focus changes
    // This ensures getEnabledComponentsForSession() is called with the correct focused session
    const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);

    // Subscribe to visibleSessions to keep them in the pool
    const visibleSessions = useSessionStore((state) => state.visibleSessions);

    // Get audio device IDs from app store
    const audioDevices = useAppStore((state) => state.audioDevices);

    // Get current room SID for setting attachedToProctorRoomSid
    const roomSid = useAppStore((state) => state.roomSid);

    // Track pending retry frames for cleanup
    const retryFrameRef = useRef([]);

    // Memoize the SID array to avoid re-creating on every render
    const sessionSidArray = useMemo(
        () => sessionSids ? sessionSids.split(',') : [],
        [sessionSids]
    );

    // Set attachedToProctorRoomSid for each session when roomSid or session list changes.
    // Uses getState() to read session data without subscribing to the sessions object.
    // Includes a deferred retry for newly added sessions whose viewerRef may not be
    // populated yet (InPortal needs one render cycle to mount the SessionViewer).
    useEffect(() => {
        if (!roomSid) return;

        const applyRoomSid = () => {
            const currentSessions = useSessionStore.getState().sessions;
            const pendingSids = [];

            sessionSidArray.forEach((sessionSid) => {
                const session = currentSessions[sessionSid];
                const viewerRef = session?.viewerRef;

                if (viewerRef?.current?.setAttachedToProctorRoomSid) {
                    viewerRef.current.setAttachedToProctorRoomSid(roomSid);
                } else if (viewerRef) {
                    // viewerRef exists but .current isn't populated yet — needs retry
                    pendingSids.push(sessionSid);
                }
            });

            return pendingSids;
        };

        // First attempt — some viewerRefs may not be ready yet
        const pendingSids = applyRoomSid();

        // Deferred retry for sessions whose InPortal hasn't mounted yet.
        // requestAnimationFrame gives React one render cycle to mount the SessionViewer.
        if (pendingSids.length > 0) {
            const frameId = requestAnimationFrame(() => {
                const currentSessions = useSessionStore.getState().sessions;
                pendingSids.forEach((sessionSid) => {
                    const session = currentSessions[sessionSid];
                    if (session?.viewerRef?.current?.setAttachedToProctorRoomSid) {
                        session.viewerRef.current.setAttachedToProctorRoomSid(roomSid);
                    }
                });
            });
            retryFrameRef.current.push(frameId);
        }

        return () => {
            retryFrameRef.current.forEach(cancelAnimationFrame);
            retryFrameRef.current = [];
        };
    }, [sessionSidArray, roomSid]);

    // PR-3412: Update viewMode for each session based on focus state
    // This prevents cross-talk by ensuring only focused session has viewMode='focus'
    // Uses getState() to read session data without subscribing to the sessions object.
    // Includes a deferred retry for newly added sessions whose viewerRef may not be ready.
    useEffect(() => {
        const applyViewMode = () => {
            const currentSessions = useSessionStore.getState().sessions;
            const pendingSids = [];

            sessionSidArray.forEach((sessionSid) => {
                const session = currentSessions[sessionSid];
                const viewerRef = session?.viewerRef;
                const isFocused = sessionSid === focusedSessionSid;
                const forcedViewMode = isFocused ? 'focus' : 'overview';

                if (viewerRef?.current?.setViewMode) {
                    viewerRef.current.setViewMode(forcedViewMode);
                } else if (viewerRef) {
                    pendingSids.push({ sessionSid, forcedViewMode });
                }
            });

            return pendingSids;
        };

        // First attempt
        const pendingSids = applyViewMode();

        // Deferred retry for sessions whose InPortal hasn't mounted yet
        if (pendingSids.length > 0) {
            const frameId = requestAnimationFrame(() => {
                const currentSessions = useSessionStore.getState().sessions;
                pendingSids.forEach(({ sessionSid, forcedViewMode }) => {
                    const session = currentSessions[sessionSid];
                    if (session?.viewerRef?.current?.setViewMode) {
                        session.viewerRef.current.setViewMode(forcedViewMode);
                    }
                });
            });
            retryFrameRef.current.push(frameId);
        }
    }, [sessionSidArray, focusedSessionSid]);

    return (
        <>
            {/* 
                Render all SessionViewer instances into InPortals.
                
                IMPORTANT: React's reconciliation with key={sessionSid} ensures:
                - Existing sessions are REUSED (same key = same component instance)
                - New sessions are CREATED
                - Removed sessions are UNMOUNTED
                
                InPortals render components into detached DOM nodes that persist
                across moves. LiveKit connections, component state, and event listeners
                are preserved because components never remount.
                
                POOL FILTERING: Only keeps sessions that are:
                - In 'testing' or 'review' locations (active sessions that need live connections)
                - Currently focused (to maintain state when viewing)
                Other sessions (waiting, checkout, reconnect, sentback) are unmounted to save resources.
                
                NOTE: Session data is read via getState() to avoid subscribing to the
                full sessions object. The component re-renders when the SID list, focus,
                visibility, or audio devices change — which covers all cases where the
                pool's rendered output needs to update.
            */}
            {sessionSidArray.map((sessionSid) => {
                const session = useSessionStore.getState().sessions[sessionSid];
                if (!session) return null;

                const viewerProps = session?.viewerProps;
                const viewerRef = session?.viewerRef;

                // Skip if no viewer props configured
                if (!viewerProps) return null;

                // Check if session is in visibleSessions array
                const isInVisibleSessions = visibleSessions.some(
                    (visibleSession) => visibleSession.sessionSid === sessionSid
                );

                // Only keep sessions in testing/review locations, if focused, or if in visibleSessions
                const shouldKeepInPool =
                    session.location === 'testing' ||
                    session.location === 'review' ||
                    sessionSid === focusedSessionSid ||
                    isInVisibleSessions;

                if (!shouldKeepInPool) {
                    // Session not in pool - React will handle unmounting cleanly
                    return null;
                }

                // Get session-specific configuration overrides
                const sessionConfig = getEnabledComponentsForSession(sessionSid);

                // Merge default enabled components with session-specific overrides
                const enabledComponents = {
                    ...viewerProps.enabledComponents, // Defaults from session store
                    ...sessionConfig // Session-specific overrides (without video)
                };

                // Extract video config separately to merge at the top level
                const { video: sessionVideo, ...sessionConfigWithoutVideo } = sessionConfig;
                const mergedEnabledComponents = {
                    ...viewerProps.enabledComponents,
                    ...sessionConfigWithoutVideo
                };

                // Merge video configuration
                const videoConfig = sessionVideo || viewerProps.video;

                // Get or create portal node for this session
                const portalNode = getOrCreatePortalNode(sessionSid);

                return (
                    <portals.InPortal key={sessionSid} node={portalNode}>
                        <SessionViewer
                            ref={viewerRef}
                            {...viewerProps}
                            enabledComponents={mergedEnabledComponents}
                            video={videoConfig}
                            audioInputDeviceId={audioDevices?.inputDeviceId}
                            audioOutputDeviceId={audioDevices?.outputDeviceId}
                        />
                    </portals.InPortal>
                );
            })}
        </>
    );
}
